import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Card,
  Typography,
  Table,
  Spin,
  Tooltip,
  Popover,
  Space,
  Button,
  message,
} from "antd";
import {
  FileSearchOutlined,
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { successAction, types } from "@authzsvc/api-lib";
import {
  resolveAuditsForEntity,
  selectGroupById,
  getIdentityById,
  getAccountById,
  getApplicationById,
} from "../../actions/api";
import { showErrorNotification } from "../../util";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { TargetObjectTypes } from "../../models/targetObjectTypes";
import { sidebar_identities, sidebar_applications } from "../../config";

// UTC plugin required as dependency for timezone plugin
dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_AUDIT_TABLE_QUERY_FIELDS = [
  "time",
  "description",
  "authorized",
  "operationType",
  "authorizationReason",
  "targetObjectId1",
  "targetObjectId2",
  "targetObjectType1",
  "targetObjectType2",
  "parentObjectId",
  "parentObjectType",
  "displayName",
  "targetObject1",
  "targetObject2",
];

const AUDIT_LOGS_LIMIT = 100;

const EntityAuditLogs = ({ selectedGroup }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [auditLogs, setAuditLogs] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [popupContent, setPopupContent] = useState({});

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      const entityId = selectedGroup?.id || id;
      const { payload, type } = await dispatch(
        resolveAuditsForEntity(entityId, {
          limit: AUDIT_LOGS_LIMIT,
          field: DEFAULT_AUDIT_TABLE_QUERY_FIELDS,
        })
      );
      if (payload && type === successAction(types.RESOLVE_AUDITS_FOR_ENTITY)) {
        setAuditLogs(payload.data);
      } else {
        showErrorNotification(payload.title, payload.data.message);
      }
      setIsLoading(false);
    };
    getData();
  }, [dispatch, id, selectedGroup]);

  const showIdentifier = async (type, targetId) => {
    if (!type || !targetId) return;

    setPopupContent((prev) => ({
      ...prev,
      [targetId]: "Loading...",
    }));

    try {
      let response;
      if (type === TargetObjectTypes.egroups) {
        response = await dispatch(selectGroupById(targetId));
        if (response.type === successAction(types.GET_GROUP_BY_ID)) {
          if (response.payload.data) {
            const group = response.payload.data;
            const displayValue =
              group.groupIdentifier ||
              group.displayName ||
              group.id ||
              targetId;
            setPopupContent((prev) => ({
              ...prev,
              [targetId]: displayValue,
            }));
          } else {
            setPopupContent((prev) => ({
              ...prev,
              [targetId]: `Group ${targetId}`,
            }));
          }
        } else {
          setPopupContent((prev) => ({
            ...prev,
            [targetId]: "Failed to load group details",
          }));
        }
      } else if (type === TargetObjectTypes.identities) {
        response = await dispatch(getIdentityById(targetId));
        if (response.type === successAction(types.GET_IDENTITY_BY_ID)) {
          if (response.payload.data) {
            const identity = response.payload.data;
            const displayValue =
              identity.upn || identity.displayName || identity.id || targetId;
            setPopupContent((prev) => ({
              ...prev,
              [targetId]: displayValue,
            }));
          } else {
            setPopupContent((prev) => ({
              ...prev,
              [targetId]: `Identity ${targetId}`,
            }));
          }
        } else {
          setPopupContent((prev) => ({
            ...prev,
            [targetId]: "Failed to load identity details",
          }));
        }
      } else if (type === TargetObjectTypes.accounts) {
        response = await dispatch(getAccountById(targetId));
        if (response.type === successAction(types.GET_ACCOUNT_BY_ID)) {
          if (response.payload.data) {
            const account = response.payload.data;
            const displayValue = account.displayName || account.id || targetId;
            setPopupContent((prev) => ({
              ...prev,
              [targetId]: displayValue,
            }));
          } else {
            setPopupContent((prev) => ({
              ...prev,
              [targetId]: `Account ${targetId}`,
            }));
          }
        } else {
          setPopupContent((prev) => ({
            ...prev,
            [targetId]: "Failed to load account details",
          }));
        }
      } else if (type === TargetObjectTypes.applications) {
        response = await dispatch(getApplicationById(targetId));
        if (response.type === successAction(types.GET_APPLICATION)) {
          if (response.payload.data) {
            const application = response.payload.data;
            const displayValue =
              application.displayName || application.id || targetId;
            setPopupContent((prev) => ({
              ...prev,
              [targetId]: displayValue,
            }));
          } else {
            setPopupContent((prev) => ({
              ...prev,
              [targetId]: `Application ${targetId}`,
            }));
          }
        } else {
          setPopupContent((prev) => ({
            ...prev,
            [targetId]: "Failed to load application details",
          }));
        }
      }
    } catch (error) {
      setPopupContent((prev) => ({
        ...prev,
        [targetId]: "Error loading details",
      }));
    }
  };

  const getIdentifierRegex = (type) => {
    switch (type) {
      case TargetObjectTypes.egroups:
        return /GroupIdentifier='([^']+)'/i;
      case TargetObjectTypes.identities:
        return /Upn='([^']+)'/i;
      case TargetObjectTypes.accounts:
        return /DisplayName='([^']+)'/i;
      case TargetObjectTypes.applications:
        return /DisplayName='([^']+)'/i;
      case TargetObjectTypes.roles:
        return /Name='([^']+)'/i;
      default:
        return null;
    }
  };

  const getTargetUrl = (type, id, applicationId, targetObjectId) => {
    switch (type) {
      case TargetObjectTypes.egroups:
        return {
          url: `/group/${id}/details`,
          targetBlank: false,
        };
      case TargetObjectTypes.identities:
        return {
          url: `${sidebar_identities}/identities/${id}`,
          targetBlank: true,
        };
      case TargetObjectTypes.roles:
        if (applicationId && targetObjectId) {
          return {
            url: `${sidebar_applications}/manage/${applicationId}/role/${targetObjectId}`,
            targetBlank: true,
          };
        }
        return null;
      default:
        return null;
    }
  };

  const parseIdentifier = (type, targetObject, targetObjectId) => {
    if (!type || !targetObject) {
      return null;
    }

    let identifier = null;
    let applicationId = null;

    try {
      const parsed = JSON.parse(targetObject);
      switch (type) {
        case TargetObjectTypes.egroups:
          identifier = parsed.groupIdentifier;
          break;
        case TargetObjectTypes.identities:
          identifier = parsed.upn;
          break;
        case TargetObjectTypes.accounts:
          identifier = parsed.displayName;
          break;
        case TargetObjectTypes.applications:
          identifier = parsed.displayName;
          break;
        case TargetObjectTypes.roles:
          identifier = parsed.name;
          applicationId = parsed.applicationId;
          break;
        default:
          break;
      }
    } catch (e) {
      const identifierRegex = getIdentifierRegex(type);
      if (identifierRegex) {
        const identifierMatch = identifierRegex.exec(targetObject);
        identifier = identifierMatch ? identifierMatch[1] : null;
      }
    }
    return {
      identifier,
      targetUrl: getTargetUrl(type, identifier, applicationId, targetObjectId),
      applicationId,
    };
  };

  const prepareTargetElement = (type, targetObject, currentGroup) => {
    if (!type || !targetObject) {
      return null;
    }

    return (
      <>
        {targetObject.targetUrl && currentGroup !== targetObject.identifier ? (
          <a
            href={targetObject.targetUrl?.url}
            {...(targetObject.targetUrl?.targetBlank
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
          >
            {type} ({targetObject.identifier})
          </a>
        ) : (
          <span>
            {type} ({targetObject.identifier})
          </span>
        )}
      </>
    );
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(
      () => {
        message.success(`${label} copied to clipboard`);
      },
      () => {
        message.error(`Failed to copy ${label}`);
      }
    );
  };

  const prepareTargetContent = (
    type,
    targetObject,
    targetObjectId,
    currentGroup
  ) => {
    if (!type) return null;

    if (TargetObjectTypes[type]) {
      if (targetObject && targetObject.identifier) {
        return prepareTargetElement(type, targetObject, currentGroup);
      } else if (targetObjectId) {
        if (
          type === TargetObjectTypes.roles &&
          targetObject &&
          targetObject.applicationId
        ) {
          return (
            <a
              href={`${sidebar_applications}/manage/${targetObject.applicationId}/role/${targetObjectId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ cursor: "pointer", color: "#1890ff" }}
            >
              {type} ({targetObjectId})
            </a>
          );
        }

        const popupData = popupContent[targetObjectId];
        const displayText =
          typeof popupData === "object"
            ? popupData.displayValue
            : popupData || "Click to load details";

        return (
          <Popover
            content={displayText}
            trigger="click"
            onOpenChange={(visible) => {
              if (visible && !popupContent[targetObjectId]) {
                showIdentifier(type, targetObjectId);
              }
            }}
          >
            <span
              style={{ cursor: "pointer", color: "#1890ff" }}
              onClick={() => showIdentifier(type, targetObjectId)}
            >
              {type} ({targetObjectId})
            </span>
          </Popover>
        );
      } else {
        return <span>{type}</span>;
      }
    } else {
      return (
        <span>
          {type}
          {targetObjectId ? ` (${targetObjectId})` : ""}
        </span>
      );
    }
  };

  const columns = [
    {
      title: "Time",
      dataIndex: "time",
      key: "time",
      render: (time) => (
        <span>
          {dayjs(time).tz("Europe/Zurich").format("DD/MM/YYYY, h:mm:ss a")}
        </span>
      ),
    },
    {
      title: "Caller",
      dataIndex: "displayName",
      key: "caller",
      render: (displayName) => displayName && <span>{displayName}</span>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Operation",
      dataIndex: "operation",
      key: "operation",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={record.authorized}>
            {record.authorized === "Authorized" ? (
              <CheckOutlined
                style={{
                  color: "#52c41a",
                }}
              />
            ) : (
              <CloseOutlined
                style={{
                  color: "#ff4d4f",
                }}
              />
            )}
          </Tooltip>
          <span>{record.operationType}</span>
        </Space>
      ),
    },
    {
      title: "Reason",
      dataIndex: "authorizationReason",
      key: "reason",
      render: (reason) => reason && <span>{reason}</span>,
    },
    {
      title: "Target 1",
      dataIndex: "target1",
      key: "target1",
      render: (_, record) => {
        const firstTargetObject = parseIdentifier(
          record.targetObjectType1,
          record.targetObject1,
          record.targetObjectId1
        );
        return (
          <Space size="small">
            {prepareTargetContent(
              record.targetObjectType1,
              firstTargetObject,
              record.targetObjectId1,
              selectedGroup?.groupIdentifier
            )}
            {record.targetObject1 && (
              <Tooltip title="Copy JSON to clipboard">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() =>
                    copyToClipboard(record.targetObject1, "Target 1 JSON")
                  }
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: "Target 2",
      dataIndex: "target2",
      key: "target2",
      render: (_, record) => {
        const secondTargetObject = parseIdentifier(
          record.targetObjectType2,
          record.targetObject2,
          record.targetObjectId2
        );
        return (
          <Space size="small">
            {prepareTargetContent(
              record.targetObjectType2,
              secondTargetObject,
              record.targetObjectId2,
              selectedGroup?.groupIdentifier
            )}
            {record.targetObject2 && (
              <Tooltip title="Copy JSON to clipboard">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() =>
                    copyToClipboard(record.targetObject2, "Target 2 JSON")
                  }
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Card
      title={
        selectedGroup && (
          <Typography.Title level={4} style={{ margin: 0 }}>
            <FileSearchOutlined style={{ marginRight: 8, color: "#bfbfbf" }} />
            Audit logs
          </Typography.Title>
        )
      }
    >
      <Spin spinning={isLoading}>
        {auditLogs !== undefined && !isLoading && (
          <Table
            columns={columns}
            dataSource={auditLogs}
            pagination={false}
            rowKey="id"
          />
        )}
      </Spin>
    </Card>
  );
};

export default EntityAuditLogs;
